import React, { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { useNavigate } from 'react-router';
import { Compass, ArrowLeft } from 'lucide-react';

const BG_IMAGE =
  "https://images.unsplash.com/photo-1659889259913-78cbb4040b8d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtaXN0eSUyMG1vdW50YWluJTIwZm9nJTIwZXRoZXJlYWwlMjBsYW5kc2NhcGUlMjB3YXJtfGVufDF8fHx8MTc3NzMyMjY1NXww&ixlib=rb-4.1.0&q=80&w=1080";

// Floating coordinate dot
const CoordDot = ({
  x, y, delay, label,
}: {
  x: string; y: string; delay: number; label: string;
}) => (
  <motion.div
    className="absolute flex items-center gap-2 pointer-events-none"
    style={{ left: x, top: y }}
    initial={{ opacity: 0, scale: 0.7 }}
    animate={{ opacity: [0, 0.5, 0.3], scale: [0.7, 1, 0.95] }}
    transition={{ duration: 3, delay, repeat: Infinity, repeatType: 'mirror', ease: 'easeInOut' }}
  >
    <div className="w-[5px] h-[5px] rounded-full bg-[#FBF9F6]/40" />
    <span
      className="text-[#FBF9F6]/30 tracking-[0.18em] uppercase font-light"
      style={{ fontSize: '9px' }}
    >
      {label}
    </span>
  </motion.div>
);

// Thin crosshair corner mark
const CornerMark = ({ position }: { position: 'tl' | 'tr' | 'bl' | 'br' }) => {
  const base = 'absolute z-20 w-5 h-5 border-[#FBF9F6]/20';
  const pos = {
    tl: 'top-8 left-8 border-t border-l',
    tr: 'top-8 right-8 border-t border-r',
    bl: 'bottom-8 left-8 border-b border-l',
    br: 'bottom-8 right-8 border-b border-r',
  }[position];
  return <div className={`${base} ${pos}`} />;
};

export function NotFound() {
  const navigate = useNavigate();
  const [compassAngle, setCompassAngle] = useState(0);

  // Compass drift
  useEffect(() => {
    const interval = setInterval(() => {
      setCompassAngle(a => a + (Math.random() * 30 - 15));
    }, 1800);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen w-full relative overflow-hidden flex items-center justify-center bg-[#1a110a]">

      {/* Background image */}
      <img
        src={BG_IMAGE}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
      />

      {/* Gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#412F21]/75 via-[#412F21]/55 to-[#1a110a]/90 z-10" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#1a110a]/30 via-transparent to-[#1a110a]/30 z-10" />

      {/* Subtle grid */}
      <svg className="absolute inset-0 w-full h-full z-10 opacity-[0.03] pointer-events-none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="grid404" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
            <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#FBF9F6" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid404)" />
      </svg>

      {/* Corner marks */}
      <CornerMark position="tl" />
      <CornerMark position="tr" />
      <CornerMark position="bl" />
      <CornerMark position="br" />

      {/* Floating coordinate labels */}
      <CoordDot x="12%" y="22%" delay={0.2} label="??°??' N" />
      <CoordDot x="72%" y="18%" delay={1.1} label="??°??' E" />
      <CoordDot x="8%"  y="72%" delay={0.7} label="Alt. unknown" />
      <CoordDot x="74%" y="75%" delay={1.5} label="Signal lost" />

      {/* KOVA wordmark */}
      <div className="absolute top-8 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-2">
        <span
          className="text-[#FBF9F6]/70 tracking-[0.55em] uppercase font-medium"
          style={{ fontSize: '14px' }}
        >
          KOVA
        </span>
        <div className="w-5 h-[1px] bg-[#FBF9F6]/20" />
      </div>

      {/* Central content */}
      <div className="relative z-20 flex flex-col items-center text-center px-8">

        {/* Compass icon */}
        <div className="mb-8">
          <motion.div
            animate={{ rotate: compassAngle }}
            transition={{ duration: 1.6, ease: [0.34, 1.56, 0.64, 1] }}
            className="w-12 h-12 rounded-full border border-[#FBF9F6]/15 flex items-center justify-center
                       bg-white/5 backdrop-blur-sm mx-auto"
          >
            <Compass size={20} strokeWidth={1} className="text-[#FBF9F6]/50" />
          </motion.div>
        </div>

        {/* 404 number */}
        <div className="relative mb-6 select-none">
          <p
            className="text-[#FBF9F6]/80 tracking-[0.35em] uppercase font-light absolute -top-5 left-1/2 -translate-x-1/2 whitespace-nowrap"
            style={{ fontSize: '10px' }}
          >
            Error · Route not found
          </p>

          <h1
            className="text-[#FBF9F6] font-medium tracking-tight relative"
            style={{
              fontSize: 'clamp(80px, 15vw, 140px)',
              letterSpacing: '-0.03em',
            }}
          >
            404
          </h1>
        </div>

        {/* Divider */}
        <div className="w-16 h-[1px] bg-gradient-to-r from-transparent via-[#FBF9F6]/30 to-transparent mb-7" />

        {/* Copy */}
        <div className="space-y-3 mb-10 max-w-xs">
          <p
            className="text-[#FBF9F6] font-medium tracking-wide"
            style={{ fontSize: '18px' }}
          >
            You weren't meant to see this.
          </p>
          <p
            className="text-[#FBF9F6]/45 font-light"
            style={{ fontSize: '13px', lineHeight: '1.7' }}
          >
            Some destinations remain unmapped.
            <br />
            This path doesn't exist — at least not yet.
          </p>
        </div>

        {/* CTA */}
        <div className="flex flex-col items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-3 px-8 py-3.5 relative overflow-hidden group cursor-pointer"
            style={{
              background: 'rgba(251,249,246,0.08)',
              border: '1px solid rgba(251,249,246,0.15)',
              backdropFilter: 'blur(12px)',
              borderRadius: '3px',
            }}
          >
            <ArrowLeft size={13} strokeWidth={1.5} className="text-[#FBF9F6]/60" />
            <span
              className="text-[#FBF9F6]/70 tracking-[0.28em] uppercase font-medium"
              style={{ fontSize: '10px' }}
            >
              Return to Kova
            </span>
          </button>

          {/* Tiny hint */}
          <p
            className="text-[#FBF9F6]/90 tracking-[0.2em] uppercase font-light"
            style={{ fontSize: '8px' }}
          >
            Tip: some eyes are better left closed.
          </p>
        </div>
      </div>

    </div>
  );
}
